import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Autocomplete, TextField } from '@mui/material';
import api from '../../services/api';
import { KpiItem, StatusPill, Tone } from '../../components/ui/PageKit';
export { KpiStrip, PageHeader, Section, StatusPill, EmptyState } from '../../components/ui/PageKit';

// Helpers de la vertical fábrica.

export type ProductOption = { id: number; name: string; sku?: string; price?: number; cost?: number; unit?: string };

let productsCache: Promise<ProductOption[]> | null = null;

/** Catálogo completo una sola vez por sesión de página (una fábrica tiene decenas de productos, no miles). */
export function loadProducts(force = false): Promise<ProductOption[]> {
  if (!productsCache || force) {
    productsCache = api.getProducts({ page: 1, pageSize: 1000 })
      .then(r => (r.products || []).map((p: any) => ({
        id: p.id, name: p.name, sku: p.sku, price: p.price, cost: p.cost, unit: p.unitOfMeasure,
      })))
      .catch(e => { productsCache = null; throw e; });
  }
  return productsCache;
}

export function useProducts(): ProductOption[] {
  const [products, setProducts] = useState<ProductOption[]>([]);
  useEffect(() => { loadProducts().then(setProducts).catch(() => setProducts([])); }, []);
  return products;
}

export const ProductSelect: React.FC<{
  value?: number | null;
  onChange: (id: number | null, product: ProductOption | null) => void;
  label?: string;
  required?: boolean;
  size?: 'small' | 'medium';
}> = ({ value, onChange, label = 'Producto', required, size = 'small' }) => {
  const products = useProducts();
  const selected = products.find(p => p.id === value) || null;
  return (
    <Autocomplete
      size={size}
      options={products}
      value={selected}
      getOptionLabel={o => (o.sku ? `${o.name} · ${o.sku}` : o.name)}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, v) => onChange(v?.id ?? null, v)}
      renderInput={params => <TextField {...params} label={label} required={required} />}
    />
  );
};

const nf0 = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtQty = (n?: number | null) => (n == null ? '—' : nf0.format(n));
export const fmtMoney = (n?: number | null) => (n == null ? '—' : `$${nf0.format(n)}`);
/** Plata con centavos: costos unitarios de piezas chicas (un tapón cuesta $5,20). */
export const fmtMoney2 = (n?: number | null) => (n == null ? '—' : `$${nf2.format(n)}`);
export const fmtPct = (n?: number | null, digits = 0) => (n == null ? '—' : `${(n * 100).toFixed(digits).replace('.', ',')}%`);

/** yyyy-mm-dd del día de hoy en Argentina. */
export function todayIso(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });
}

export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** yyyy-mm-dd de una fecha "de día" que viene del backend (2026-10-29T00:00:00Z). */
export const isoDay = (s?: string | null) => (s ? s.slice(0, 10) : '');

/** Quincena que contiene la fecha: 1 al 15 o 16 a fin de mes. */
export function fortnightOf(iso: string): { from: string; to: string } {
  const [y, m, d] = iso.split('-').map(Number);
  if (d <= 15) return { from: `${iso.slice(0, 8)}01`, to: `${iso.slice(0, 8)}15` };
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${iso.slice(0, 8)}16`, to: `${iso.slice(0, 8)}${String(last).padStart(2, '0')}` };
}

export const errorMessage = (e: any, fallback: string) => e?.response?.data?.message || fallback;

/** Un indicador dentro de <KpiStrip>. El color solo se usa cuando hay algo que mirar. */
export const Kpi: React.FC<{ label: string; value: React.ReactNode; hint?: React.ReactNode; color?: string; tone?: Tone }> = ({ label, value, hint, color, tone }) => (
  <KpiItem label={label} value={value} hint={hint} tone={tone ?? toneOfColor(color)} />
);

/** Colores sueltos de las pantallas viejas → tono del kit. */
function toneOfColor(color?: string): Tone | undefined {
  if (!color) return undefined;
  const c = color.toLowerCase();
  if (['#d32f2f', '#c62828', 'error.main'].includes(c)) return 'bad';
  if (['#ed6c02', '#e65100', 'warning.main'].includes(c)) return 'warn';
  if (['#2e7d32', '#1b5e20', 'success.main'].includes(c)) return 'ok';
  return 'info';
}

/** Número desde un TextField. Con coma se toma como formato argentino (1.234,5); si no, tal cual (0.95). */
export const num = (v: string | number | null | undefined): number => {
  if (v == null || v === '') return 0;
  const s = String(v).trim();
  const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
  return Number.isFinite(n) ? n : 0;
};

export type MaterialOption = { id: number; name: string; unit?: string; stock: number };

let materialsCache: Promise<MaterialOption[]> | null = null;

export function loadMaterials(force = false): Promise<MaterialOption[]> {
  if (!materialsCache || force) {
    materialsCache = api.getMaterials({ page: 1, pageSize: 1000 })
      .then(r => (r.materials || []).map((m: any) => ({ id: m.id, name: m.name, unit: m.unit, stock: Number(m.currentStock ?? 0) })))
      .catch(e => { materialsCache = null; throw e; });
  }
  return materialsCache;
}

export function useMaterials(force = false): MaterialOption[] {
  const [materials, setMaterials] = useState<MaterialOption[]>([]);
  useEffect(() => { loadMaterials(force).then(setMaterials).catch(() => setMaterials([])); }, [force]);
  return materials;
}

export const MaterialSelect: React.FC<{
  value?: number | null;
  onChange: (id: number | null, material: MaterialOption | null) => void;
  label?: string;
  required?: boolean;
  options?: MaterialOption[];
}> = ({ value, onChange, label = 'Materia prima', required, options }) => {
  const loaded = useMaterials();
  const materials = options ?? loaded;
  const selected = materials.find(m => m.id === value) || null;
  return (
    <Autocomplete
      size="small"
      options={materials}
      value={selected}
      getOptionLabel={o => `${o.name} · ${fmtQty(o.stock)} ${o.unit || ''}`.trim()}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, v) => onChange(v?.id ?? null, v)}
      renderInput={params => <TextField {...params} label={label} required={required} />}
    />
  );
};

export const LEVEL_CHIP: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  verde: { label: 'Verde', color: 'success' },
  amarillo: { label: 'Amarillo', color: 'warning' },
  rojo: { label: 'Rojo', color: 'error' },
  'sin-configurar': { label: 'Sin niveles', color: 'default' },
};

export const DUE_CHIP: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }> = {
  ok: { label: 'Al día', color: 'success' },
  proximo: { label: 'Próximo', color: 'warning' },
  vencido: { label: 'Vencido', color: 'error' },
  programado: { label: 'Programado', color: 'info' },
  'en curso': { label: 'En curso', color: 'warning' },
};

export const EQUIPMENT_STATUS: Record<string, { label: string; color: 'success' | 'warning' | 'error' }> = {
  Disponible: { label: 'Disponible', color: 'success' },
  EnMantenimiento: { label: 'En mantenimiento', color: 'warning' },
  FueraDeServicio: { label: 'Fuera de servicio', color: 'error' },
};

const CHIP_TONE: Record<string, Tone> = { success: 'ok', warning: 'warn', error: 'bad', info: 'info', primary: 'info', secondary: 'info', default: 'neutral' };

export const StatusChip: React.FC<{ map: Record<string, { label: string; color: any }>; value?: string | null }> = ({ map, value }) => {
  if (!value) return <>—</>;
  const v = map[value] ?? { label: value, color: 'default' };
  return <StatusPill tone={CHIP_TONE[v.color] ?? 'neutral'}>{v.label}</StatusPill>;
};

/** Achica la foto en el celular antes de subirla (1600 px, JPEG): sube rápido aunque haya poca señal. */
export async function compressPhoto(file: File): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d')!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((res, rej) => canvas.toBlob(b => (b ? res(b) : rej(new Error('blob'))), 'image/jpeg', 0.82));
  } catch {
    return file;
  }
}

// ==================== Vistas de una pantalla: solapas o menú ====================

/**
 * true dentro del sistema de planta (PlantLayout): cada vista de una pantalla es un ítem del menú lateral con su
 * propia ruta (/factory/production/indicadores) y la pantalla no muestra solapas. En el resto, solapas como siempre.
 */
export const FlatNavContext = createContext(false);

export type PageView = { key: string; label: string; description?: React.ReactNode };

export function usePageView(views: PageView[], initial = 0) {
  const flat = useContext(FlatNavContext);
  const { view } = useParams<{ view?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const idx = views.findIndex(v => v.key === view);
  const [local, setLocal] = useState(idx >= 0 ? idx : initial);
  const tab = flat ? (idx >= 0 ? idx : initial) : local;
  const setTab = (i: number) => {
    if (!flat) { setLocal(i); return; }
    const base = view ? location.pathname.slice(0, location.pathname.lastIndexOf('/')) : location.pathname.replace(/\/$/, '');
    navigate(`${base}/${views[i].key}`);
  };
  return { tab, setTab, showTabs: !flat, flat, current: views[tab] };
}
